import React, { useMemo } from "react";
import PropTypes from "prop-types";

import LightSelect from "../../CompanyView/LightSelect";
import { UseCompanyWorkspaces, UseSelectedWorkspace } from "../../../hooks/company";
import useLeaderboardData, { periodBucket } from "../../../components/LeaderBoard/LeaderboardTable/data";
import "./customize.css";

/**
 * The leaderboard's Basics workspace picker and its Setup tab.
 *
 * Both read the SAME live column list the card renders from, so nothing in the
 * editor can name a metric the chosen workspaces don't track. Change the
 * workspaces and every dropdown here re-reads itself.
 */

const Pick = ({ value, options, onChange, placeholder }) => (
  <LightSelect
    width="100%"
    height={36}
    fontSize={12.5}
    radius={8}
    menuWidth={336}
    placeholder={placeholder}
    value={value || ""}
    options={options}
    onChange={(o) => onChange(o.value)}
  />
);

Pick.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

Pick.defaultProps = { value: "", placeholder: "Choose" };

/** The workspaces this board pools. Empty = the one the dashboard is on. */
export const WorkspacePicker = ({ draft, onChange }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const selected = UseSelectedWorkspace();
  const picked = (draft.workspaceIds || []).map(String);

  const toggle = (id) => {
    const next = picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id];
    onChange({ ...draft, workspaceIds: next });
  };

  return (
    <div className="gmbw-fld">
      <label>Workspaces</label>
      <div className="gmbw-wspick">
        {workspaces.map((w) => {
          const id = String(w._id);
          const on = picked.includes(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={on}
              className={`gmbw-wsopt${on ? " on" : ""}`}
              onClick={() => toggle(id)}
            >
              <span className="gmbw-wsbox">
                {on && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </span>
              <span className="gmbw-wsnm">{w.name}</span>
            </button>
          );
        })}
      </div>
      <p className="gmbw-hint">
        {picked.length > 1
          ? "Everyone from these workspaces in one ranking. People are still ranked individually — a workspace is never a row."
          : `Leave all clear to follow the board’s own workspace${selected?.name ? ` (${selected.name})` : ""}.`}
      </p>
    </div>
  );
};

WorkspacePicker.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

/** Rank by · Order · Tie-break · Who appears — the rules of the ranking. */
const LeaderboardSetup = ({ draft, periodIdx = 3, onChange }) => {
  const selected = UseSelectedWorkspace();
  const ids = useMemo(() => {
    const picked = (draft.workspaceIds || []).filter(Boolean).map(String);
    if (picked.length) return picked;
    return selected?._id ? [String(selected._id)] : [];
  }, [draft.workspaceIds, selected?._id]);

  const { columns } = useLeaderboardData(ids, periodBucket(periodIdx), draft.metricPlan || null);

  /* Points is always available — it is what every workspace scores in. */
  const rankOptions = useMemo(
    () => [...columns.map((c) => ({ value: c.key, label: c.label })), { value: "pts", label: "Points" }],
    [columns],
  );
  const closedKey = useMemo(() => {
    const hit = columns.find((c) =>
      /\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/i.test(String(c.label || c.key || "")),
    );
    return hit ? hit.key : null;
  }, [columns]);
  const tieOptions = useMemo(
    () => [{ value: "", label: closedKey ? "Closed Rate (default)" : "None" }, ...rankOptions.filter((o) => o.value !== (draft.rankBy || "pts"))],
    [rankOptions, draft.rankBy, closedKey],
  );

  const set = (patch) => onChange({ ...draft, ...patch });
  const colN = columns.length || 5;
  const qualifyOn = draft.yetiQualify !== false;
  const qualifyNeed = Math.min(Math.max(1, Number(draft.yetiQualifyNeed) || 4), Math.max(1, colN));

  return (
    <>
      <div className="gmbw-fld">
        <label>Rank by</label>
        <Pick
          value={draft.rankBy || "pts"}
          options={rankOptions}
          onChange={(v) => set({ rankBy: v, tieBreak: draft.tieBreak === v ? "" : draft.tieBreak })}
        />
        <p className="gmbw-hint">Decides who sits at number one among players who qualify.</p>
      </div>

      <div className="gmbw-fld">
        <label>Order</label>
        <Pick
          value={draft.rankOrder || "desc"}
          options={[{ value: "desc", label: "Highest first" }, { value: "asc", label: "Lowest first" }]}
          onChange={(v) => set({ rankOrder: v })}
        />
        <p className="gmbw-hint">Lowest first is for the metrics where less is better — callbacks, cancellations, days to close.</p>
      </div>

      <div className="gmbw-fld">
        <label>Tie-break</label>
        <Pick
          value={draft.tieBreak || ""}
          options={tieOptions}
          onChange={(v) => set({ tieBreak: v })}
          placeholder={closedKey ? "Closed Rate (default)" : "None"}
        />
        <p className="gmbw-hint">
          When two players tie on the rank metric, the higher Closed Rate ranks above. Leave blank to use Closed Rate automatically when that column exists.
        </p>
      </div>

      <div className="gmbw-fld">
        <div className="gmbw-bonusbar">
          <label style={{ margin: 0 }}>Yeti qualify gate</label>
          <button
            type="button"
            role="switch"
            aria-checked={qualifyOn}
            aria-label="Yeti qualify gate"
            className={`gmbw-switch${qualifyOn ? " on" : ""}`}
            onClick={() => set({ yetiQualify: !qualifyOn })}
          >
            <span className="gmbw-switch-dot" />
          </button>
        </div>
        <p className="gmbw-hint">
          A player must beat the Yeti on at least {qualifyNeed} of {colN || "N"} metrics to rank ahead of everyone who doesn&apos;t. Unqualified players still appear, below the qualified pack.
        </p>
        {qualifyOn && colN > 1 && (
          <div style={{ marginTop: 8 }}>
            <Pick
              value={String(qualifyNeed)}
              options={Array.from({ length: colN }, (_, i) => ({
                value: String(i + 1),
                label: `${i + 1} of ${colN} metrics`,
              }))}
              onChange={(v) => set({ yetiQualifyNeed: Number(v) })}
            />
          </div>
        )}
      </div>

      <div className="gmbw-fld">
        <label>Who appears</label>
        <Pick
          value={draft.whoAppears || "all"}
          options={[
            { value: "all", label: "Everyone" },
            { value: "active", label: "Only people with activity" },
          ]}
          onChange={(v) => set({ whoAppears: v })}
        />
        <p className="gmbw-hint">A new hire with nothing logged yet doesn’t have to sit at the bottom of a wall.</p>
      </div>

      <div className="gmbw-fld">
        <label>On a TV</label>
        <Pick
          value={draft.tvRotate || "rotate"}
          options={[
            { value: "rotate", label: "Rotate through everyone" },
            { value: "topfit", label: "Show the top names only" },
          ]}
          onChange={(v) => set({ tvRotate: v })}
        />
        <p className="gmbw-hint">A big team can’t all fit on a wall. Rotate shows a few names at a time and cycles through the rest.</p>
      </div>

      {(draft.tvRotate || "rotate") === "rotate" && (
        <>
          <div className="gmbw-fld">
            <label>Names per rotation</label>
            <Pick
              value={String(draft.tvRotateSize || 5)}
              options={[3, 4, 5, 6, 8].map((n) => ({ value: String(n), label: `${n} at a time` }))}
              onChange={(v) => set({ tvRotateSize: Number(v) })}
            />
          </div>
          <div className="gmbw-fld">
            <label>Rotate every</label>
            <Pick
              value={String(draft.tvRotateSecs || 15)}
              options={[10, 15, 20, 30].map((n) => ({ value: String(n), label: `${n} seconds` }))}
              onChange={(v) => set({ tvRotateSecs: Number(v) })}
            />
          </div>
        </>
      )}

      {!columns.length && (
        <p className="gmbw-hint">
          These workspaces track no metric in common, so only Points can rank the board.
        </p>
      )}
    </>
  );
};

LeaderboardSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  periodIdx: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

LeaderboardSetup.defaultProps = { periodIdx: 3 };

/* The live column list, for the Metrics switch's own description. `enabled` is
   false for every other component, so opening their editor fetches nothing. */
export const useLiveColumns = (draft, enabled = true) => {
  const selected = UseSelectedWorkspace();
  const ids = useMemo(() => {
    if (!enabled) return [];
    const picked = (draft.workspaceIds || []).filter(Boolean).map(String);
    if (picked.length) return picked;
    return selected?._id ? [String(selected._id)] : [];
  }, [enabled, draft.workspaceIds, selected?._id]);
  const { columns } = useLeaderboardData(ids, "month", draft.metricPlan || null);
  return { columns, count: ids.length };
};

export default LeaderboardSetup;
